import { isTruthy, pluralize } from '@ardoq/common-helpers';
import {
  DissectedRelativeDate,
  getTodayOption,
  getTomorrowOption,
  getYesterdayOption,
  TimeFrameUnit,
  dissectRelativeDate,
  formatDateShort,
  relativeDateLabelFormatters,
  TODAY_VALUE,
  TOMORROW_VALUE,
  YESTERDAY_VALUE,
} from '@ardoq/date-time';
import { IconName } from '@ardoq/icons';
import { SELECT_DATE_OPTION } from './const';
import {
  Mode,
  RelativeDatePickerMode,
  RelativeDatePickerValue,
  RelativeDatePresetMode,
} from './types';
import { getCurrentLocale } from '@ardoq/locale';
import { type I18nLocale } from '@ardoq/i18n';

export const isRelativeDate = (relativeDate: string) =>
  Boolean(dissectRelativeDate(relativeDate));

export const composeRelativeDate = ({ unit, value }: DissectedRelativeDate) => {
  const valueString = `${value}`;
  if (unit === TimeFrameUnit.HOUR_AGO)
    return 'now-Xh'.replace('X', valueString);
  if (unit === TimeFrameUnit.HOUR_FROM_NOW)
    return 'now+Xh'.replace('X', valueString);
  if (unit === TimeFrameUnit.DAY_AGO) return 'now-Xd'.replace('X', valueString);
  if (unit === TimeFrameUnit.DAY_FROM_NOW)
    return 'now+Xd'.replace('X', valueString);
  if (unit === TimeFrameUnit.MONTH_AGO)
    return 'now-Xm'.replace('X', valueString);
  if (unit === TimeFrameUnit.MONTH_FROM_NOW)
    return 'now+Xm'.replace('X', valueString);
  if (unit === TimeFrameUnit.YEAR_AGO)
    return 'now-Xy'.replace('X', valueString);
  if (unit === TimeFrameUnit.YEAR_FROM_NOW)
    return 'now+Xy'.replace('X', valueString);
};

export const isTimeFrameUnit = (value: string): value is TimeFrameUnit =>
  Object.values(TimeFrameUnit).includes(value as TimeFrameUnit);

export const getIsISODateString = (isoDateString?: string) =>
  Boolean(isoDateString && /\d{4}-\d{2}-\d{2}/.test(isoDateString));

const getPresetOptions = (
  relativeDatePickerMode: RelativeDatePickerMode,
  presetMode: RelativeDatePresetMode,
  i18nLocale: I18nLocale
) => {
  if (presetMode === RelativeDatePresetMode.SHORT) {
    return [
      getTodayOption(i18nLocale),
      getTomorrowOption(i18nLocale),
      getYesterdayOption(i18nLocale),
    ];
  }
  return [
    getTodayOption(i18nLocale),
    ...getFixedOptions(1, relativeDatePickerMode),
  ];
};

const SHORT_MODE_PRESET_VALUES: ReadonlySet<string> = new Set([
  TODAY_VALUE,
  TOMORROW_VALUE,
  YESTERDAY_VALUE,
]);

const NORMAL_MODE_PRESET_VALUES: ReadonlySet<string> = new Set<string>([
  TODAY_VALUE,
  TimeFrameUnit.HOUR_AGO,
  TimeFrameUnit.DAY_AGO,
  TimeFrameUnit.MONTH_AGO,
  TimeFrameUnit.YEAR_AGO,
  TimeFrameUnit.HOUR_FROM_NOW,
  TimeFrameUnit.DAY_FROM_NOW,
  TimeFrameUnit.MONTH_FROM_NOW,
  TimeFrameUnit.YEAR_FROM_NOW,
]);

export const getIsDefaultModeValue = (
  selectedOptionValue: RelativeDatePickerValue,
  presetMode: RelativeDatePresetMode = RelativeDatePresetMode.NORMAL
): boolean => {
  if (!selectedOptionValue) return false;
  const presetValues =
    presetMode === RelativeDatePresetMode.SHORT
      ? SHORT_MODE_PRESET_VALUES
      : NORMAL_MODE_PRESET_VALUES;
  return presetValues.has(selectedOptionValue);
};

export const getDefaultOptions = (
  selectedDate: RelativeDatePickerValue,
  relativeDatePickerMode: RelativeDatePickerMode = RelativeDatePickerMode.DATE_TIME,
  presetMode: RelativeDatePresetMode = RelativeDatePresetMode.NORMAL,
  i18nLocale: I18nLocale
) => {
  const locale = getCurrentLocale();
  const selectedDateOption =
    selectedDate && getIsISODateString(selectedDate)
      ? {
          label: formatDateShort(selectedDate, locale),
          value: selectedDate,
          ...(presetMode === RelativeDatePresetMode.SHORT && {
            leftIcon: { name: IconName.CALENDAR },
          }),
        }
      : null;
  const presetOptions = getPresetOptions(
    relativeDatePickerMode,
    presetMode,
    i18nLocale
  );
  const options = [selectedDateOption, ...presetOptions].filter(isTruthy);
  return [{ options: [SELECT_DATE_OPTION] }, { options }];
};

export const getInputModeFromValue = (
  value: string | undefined,
  presetMode: RelativeDatePresetMode = RelativeDatePresetMode.NORMAL
): Mode => {
  if (!value || getIsDefaultModeValue(value, presetMode)) return Mode.DEFAULT;
  if (isRelativeDate(value)) return Mode.RELATIVE_DATE;
  return Mode.DEFAULT;
};

const TIME_FRAME_UNIT_OPTIONS = [
  TimeFrameUnit.DAY_AGO,
  TimeFrameUnit.MONTH_AGO,
  TimeFrameUnit.YEAR_AGO,
  TimeFrameUnit.DAY_FROM_NOW,
  TimeFrameUnit.MONTH_FROM_NOW,
  TimeFrameUnit.YEAR_FROM_NOW,
];
const getTimeFrameUnitsOptions = (count: number) =>
  TIME_FRAME_UNIT_OPTIONS.map(value => ({
    value,
    label: relativeDateLabelFormatters[value](count),
  }));

export const getRelativeDateOptions = (
  count: number,
  relativeDatePickerMode: RelativeDatePickerMode = RelativeDatePickerMode.DATE_TIME,
  i18nLocale: I18nLocale
) => {
  return [
    {
      options: [SELECT_DATE_OPTION],
    },
    {
      options: [
        getTodayOption(i18nLocale),
        ...getFixedOptions(count, relativeDatePickerMode),
      ],
    },
  ];
};

const getFixedOptions = (
  count: number,
  relativeDatePickerMode: RelativeDatePickerMode = RelativeDatePickerMode.DATE_TIME
) => {
  const defaultOptions = getTimeFrameUnitsOptions(count);
  const timeFrameUnitsOptions =
    relativeDatePickerMode === RelativeDatePickerMode.DATE
      ? defaultOptions
      : [
          {
            value: TimeFrameUnit.HOUR_AGO,
            label: `${pluralize('Hour', count)} ago`,
          },
          ...defaultOptions.slice(0, 3),
          {
            value: TimeFrameUnit.HOUR_FROM_NOW,
            label: `${pluralize('Hour', count)} from now`,
          },
          ...defaultOptions.slice(3),
        ];

  return timeFrameUnitsOptions;
};
